import { ApiResult, FieldType, ViewModelBase, isEmptyOrWhitespace } from "@shoothill/core";
import { runInAction, observable, computed, action } from "mobx";
import {
    DailyProgrammeProgressUpdateByIdResponseDTO,
    DailyProgrammeProgressUpdateFile,
    DailyProgrammeProgressUpdateModel,
    FormRequestDTO,
    UpsertDailyProgrammeProgressUpdateRequestDTO,
    UpsertDailyProgrammeProgressUpdateResponseDTO,
} from "./DailyProgrammeProgressUpdateModel";
import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";

export class DailyProgrammeProgressUpdateViewModel extends ViewModelBase<DailyProgrammeProgressUpdateModel> {
    public projectId: string = "";
    public constructor(projectId: string | null, date: any) {
        super(new DailyProgrammeProgressUpdateModel(), false);
        this.setDecorators(DailyProgrammeProgressUpdateModel);
        this.model.projectId = projectId!;
        this.model.createdDate = date.toISOString();
        isEmptyOrWhitespace(this.model.id) && this.loadWithRelated();
    }

    @observable public errorMessage: string = "";

    public server: ServerViewModel = new ServerViewModel();

    @computed
    public get isFormDisabled(): boolean {
        return this.model.hasId;
    }

    @action
    public reset = () => {
        this.model.reset();
        this.server.reset();
    };

    @action
    public setDailyProgrammeProgressPhotosFile = (file: DailyProgrammeProgressUpdateFile) => {
        this.model.dailyProgrammeProgressPhotos.push(file);
    };

    @action
    public deleteDailyProgrammeProgressPhotosFile = async (index: number): Promise<void> => {
        this.model.dailyProgrammeProgressPhotos[index].isDeleted = true;
    };

    public fileChange = async (event: any): Promise<void> => {
        if (event.target.files.length > 0) {
            let data: any = {
                fileName: event.target.files[0].name,
                formFile: event.target.files[0],
            };
            event.target.value = null;
            const apiResult = await this.fileUpload(data);
            if (apiResult && apiResult.wasSuccessful) {
                let fileToDisplay: DailyProgrammeProgressUpdateFile = {
                    id: null,
                    dailyProgrammeProgressId: null,
                    fileName: data.fileName,
                    photoURL: apiResult.payload,
                    isDeleted: false,
                    createdByUserId: "",
                    createdDate: "",
                };
                this.setDailyProgrammeProgressPhotosFile(fileToDisplay);
            }
        }
    };

    public fileUpload = async (data: any): Promise<ApiResult<any>> => {
        const formData = new FormData();
        formData.append("formFile", data.formFile);
        formData.append("fileName", data.fileName);
        const apiResult = await this.Post<any>(AppUrls.Server.File.UploadFile, formData);
        if (apiResult) {
            if (!apiResult.wasSuccessful) {
                console.log(apiResult.errors);
            }
        }
        return apiResult;
    };

    public DownloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
        try {
            const apiResult = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, fileUrl, undefined, { responseType: "blob" });
            const response = apiResult as any;
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
        } catch (exception) {
            console.error(exception);
            this.setIsErrored(true);
        }
    };

    @action
    public loadWithRelated = async (): Promise<void> => {
        this.setIsLoading(true);
        const request: FormRequestDTO = {
            date: this.model.createdDate,
            projectId: this.model.projectId,
            formId: null,
        };
        let apiResult = await this.Post<any>(
            AppUrls.Server.Projects.Construction.ProgrammeUpdates.DailyProgrammeProgressUpdate.GetDailyProgrammeProgressUpdateAndRelatedByProjectId,
            request,
        );
        if (apiResult) {
            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.model.fromDto(apiResult.payload.dailyProgrammeProgressUpdate);
                    apiResult.payload.dailyProgrammeProgressUpdatePhotos.map((el: any) => this.setDailyProgrammeProgressPhotosFile(el));
                });
            } else {
                console.log(apiResult.errors);
            }
        }
    };

    @action
    public upsert = async (e?: any): Promise<ApiResult<UpsertDailyProgrammeProgressUpdateRequestDTO>> => {
        e?.preventDefault();

        if (await this.isMyModelValid()) {
            const model: any = this.model.toDto();
            let dailyProgrammeProgressPhotos: DailyProgrammeProgressUpdateFile[] = [];
            this.model.dailyProgrammeProgressPhotos.forEach((dailyProgrammeProgressPhoto) => {
                const dailyProgrammeProgress: DailyProgrammeProgressUpdateFile = {
                    id: dailyProgrammeProgressPhoto.id,
                    dailyProgrammeProgressId: dailyProgrammeProgressPhoto.dailyProgrammeProgressId,
                    fileName: dailyProgrammeProgressPhoto.fileName,
                    photoURL: dailyProgrammeProgressPhoto.photoURL,
                    createdByUserId: dailyProgrammeProgressPhoto.createdByUserId,
                    isDeleted: dailyProgrammeProgressPhoto.isDeleted,
                    createdDate: dailyProgrammeProgressPhoto.createdDate,
                };
                dailyProgrammeProgressPhotos.push(dailyProgrammeProgress);
            });

            const request: UpsertDailyProgrammeProgressUpdateRequestDTO = {
                dailyProgrammeProgressUpdate: model,
                dailyProgrammeProgressUpdatePhotos: dailyProgrammeProgressPhotos,
            };

            let apiResult = await this.Post<UpsertDailyProgrammeProgressUpdateResponseDTO>(
                AppUrls.Server.Projects.Construction.ProgrammeUpdates.DailyProgrammeProgressUpdate.UpsertDailyProgrammeProgressUpdate,
                request,
            );

            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.model.fromDto(apiResult.payload.dailyProgrammeProgressUpdate);
                    apiResult.payload.dailyProgrammeProgressUpdatePhotos.map((el: any) => this.setDailyProgrammeProgressPhotosFile(el));
                });
            }
            return apiResult;
        } else {
            this.errorMessage = "Form is not valid";
            return Promise.reject();
        }
    };

    // Validation Start

    @computed
    private get validateDailyProgrammeProgressNote() {
        const errorMessage = this.model.validateDailyProgrammeProgressNote;
        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    // End Validation

    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) === false) {
            isValid = false;
        }

        return isValid;
    };

    public doSubmit = async (e: any) => {
        e.preventDefault();

        if (await this.isModelValid()) {
            this.errorMessage = "Form is valid";
        } else {
            this.errorMessage = "Form is not valid";
        }
    };

    public async isFieldValid(fieldName: keyof FieldType<DailyProgrammeProgressUpdateModel>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        if (this.server.IsSubmitted) {
            switch (fieldName) {
                case "dailyProgrammeProgressNote": {
                    const result = this.validateDailyProgrammeProgressNote;
                    errorMessage = result.errorMessage;
                    isValid = result.isValid;
                    break;
                }
            }
        } else {
            errorMessage = "";
            isValid = true;
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
